import { useEffect, useRef, useState, useCallback } from 'react';

type UseDebouncedStateOptions<T> = {
  value: T;
  onChange: (value: T) => void;
  delay?: number;
};

export function useDebouncedState<T>({
  value,
  onChange,
  delay = 300,
}: UseDebouncedStateOptions<T>) {
  const [localValue, setLocalValue] = useState<T>(value);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChangeRef = useRef<T | null>(null);
  const hasPendingChangeRef = useRef(false);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasPendingChangeRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const updateValue = useCallback(
    (newValue: T) => {
      setLocalValue(newValue);

      pendingChangeRef.current = newValue;
      hasPendingChangeRef.current = true;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        if (pendingChangeRef.current !== null) {
          onChange(pendingChangeRef.current);
          pendingChangeRef.current = null;
          hasPendingChangeRef.current = false;
        }
        debounceRef.current = null;
      }, delay);
    },
    [onChange, delay],
  );

  const flush = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (pendingChangeRef.current !== null) {
      onChange(pendingChangeRef.current);
      pendingChangeRef.current = null;
      hasPendingChangeRef.current = false;
    }
  }, [onChange]);

  return {
    value: localValue,
    setValue: updateValue,
    flush,
  };
}
